/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { useTranslation } from "@iam/design-system/i18n";
import { Badge } from "@iam/design-system/primitives";
import { Wrench } from "lucide-react";
import type { ToolDescriptor } from "../lib/types";

export type ToolCatalogProps = {
  tools: ReadonlyArray<ToolDescriptor>;
  "data-testid"?: string;
};

/**
 * ToolCatalog renders the tenant's tool catalog as fetched from GET /v1/tools.
 * Each row shows the tool name, version, description and the scopes a caller
 * must hold — the same descriptor facts the MCP server advertises.
 */
export function ToolCatalog({ tools, "data-testid": testId }: ToolCatalogProps) {
  const t = useTranslation();
  if (tools.length === 0) {
    return (
      <div className="console-catalog console-catalog--empty" data-testid={testId}>
        <p>{t("tools.empty")}</p>
      </div>
    );
  }
  return (
    <ul className="console-catalog" data-testid={testId}>
      {tools.map((tool) => (
        <li key={`${tool.name}@${tool.version}`} className="console-catalog__row">
          <span className="console-catalog__head">
            <Wrench size={14} aria-hidden="true" />
            <code className="console-catalog__name">{tool.name}</code>
            <span className="console-catalog__version">v{tool.version}</span>
            {tool.availability === "optional" && (
              <Badge tone="info" size="sm">
                optional
              </Badge>
            )}
          </span>
          <p className="console-catalog__desc">{tool.description}</p>
          {tool.scopes.length > 0 && (
            <span className="console-catalog__scopes">
              {t("tools.scopes")}:{" "}
              {tool.scopes.map((s) => (
                <code key={s} className="console-catalog__scope">
                  {s}
                </code>
              ))}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
